import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { orderBy } from 'lodash';
import { Col, Row, ListGroup, Badge } from '../../../ui-kit';
import { IconCircleInfo, IconBan, IconBellSolid, IconPersonRunning } from '../../../ui-kit/icons';
import {
  selectCurrentDisplayedIncident,
  selectIncidentSkillAssignments
} from '../../../../state/modules/incidents/selectors';
import SkillIconList from '../../../general/SkillIconList';
import WaitingForIncidentMessage from '../shared/IncidentWidgets/WaitingForIncidentMessage';
import { secondsToHumanTime } from '../../../../utils/time';
import AvailabilityIcon from '../../../general/AvailabilityIcon';
import { IncidentResponderStatusIcon } from '../../../general/IncidentResponderStatusIcon';
import Settings from './Settings';

// Defines the color of the list item
const statusToStyle = {
  acknowledged: 'success',
  pending: null,
  rejected: 'danger',
  expired: 'danger'
};

// do not disturb indicator. A fa-ban stacked on top of a fa-bell
const DoNotDisturbIcon = () => (
  <span className="fa-layers fa-fw do-not-disturb" title={I18n.t('activerecord.attributes.user.do_not_disturb')}>
    <IconBellSolid size="sm" className="text-secondary" />
    <IconBan size="lg" className="text-danger" />
  </span>
);

// Highlights the content in blue, with white font
const onDutyStyle = {
  background: '#5959fc',
  color: '#fff',
  borderRadius: '5px',
  padding: '3px'
};

const ResponderList = ({
  responders,
  title,
  variant,
  showEstimatedTimeOfArrival,
  showAvailabilityAtIncidentCreation,
  showNicknameInsteadOfFullName,
  showCount = true
}) =>
  responders.length > 0 && (
    <ListGroup className="mb-1">
      <ListGroup.Item variant={variant}>
        <h5 className="fw-semibold">
          {showCount && (
            <Badge variant="secondary" pill>
              {responders.length}
            </Badge>
          )}{' '}
          {title}
        </h5>
      </ListGroup.Item>
      {responders.map(responder => (
        <ResponderItem
          responder={responder}
          key={responder.id}
          showEstimatedTimeOfArrival={showEstimatedTimeOfArrival}
          showAvailabilityAtIncidentCreation={showAvailabilityAtIncidentCreation}
          showNicknameInsteadOfFullName={showNicknameInsteadOfFullName}
        />
      ))}
    </ListGroup>
  );

// Split responders into groups
const splitIntoGroups = (
  responders,
  incidentSkillAssignments,
  hideRejectedAndExpiredResponses,
  hidePendingResponses,
  showSeatAllocation
) => {
  // sort so that users with a shift are always on top of each sublist
  responders = orderBy(responders, r => r.activeStandbyDutyFunctions.length, 'desc');

  let assigned = [];

  // show seat allocation if requested
  if (showSeatAllocation) {
    assigned = responders
      .map(r => [r, incidentSkillAssignments.find(assignment => assignment.membership_id === r.membership_id)])
      .filter(([_, assignment]) => assignment)
      .map(([r, assignment]) => ({ ...r, skills: orderBy(assignment.skills, 'order') }));

    // the seat allocation order is determined by the order of the first skill
    // assigned, instead of the membership display_order
    assigned = orderBy(assigned, r => r.skills[0]?.order);
  }

  const assignedIds = new Set(assigned.map(r => r.id));
  responders = responders.filter(r => !assignedIds.has(r.id));

  let hiddenResponsesCount = 0;
  if (hideRejectedAndExpiredResponses) {
    responders = responders.filter(r => r.status !== 'rejected' && r.status !== 'expired');
    hiddenResponsesCount = responders.length;
  }

  if (hidePendingResponses) {
    responders = responders.filter(r => r.status !== 'pending');
  }

  let acknowledged = responders.filter(r => r.status === 'acknowledged');
  // sort so the users that where available before the incident are always on top
  acknowledged = orderBy(acknowledged, 'available_at_incident_creation', 'desc');
  const acknowledgedAvailableTeams = acknowledged.filter(r => r.group_available_at_incident_creation);
  const acknowledgedUnavailableTeams = acknowledged.filter(r => !r.group_available_at_incident_creation);

  const pending = responders.filter(r => r.status === 'pending');
  const pendingUnavailableCount = pending.filter(r => !r.available_at_incident_creation).length;
  const rejected = responders.filter(r => r.status === 'rejected');
  // We only care about those expired who were available. Expired unavailable
  // clutter the list.
  // Note: We do not apply this filter to rejected unavailable, becase rejecting
  // requires an explicit action, while expiration happens automatically.
  const expired = responders.filter(r => r.status === 'expired' && r.available_at_incident_creation);

  // Get number of members who where not available at the moment of the incident
  const pendingAvailable = pending.filter(r => r.available_at_incident_creation);

  return {
    assigned,
    acknowledgedAvailableTeams,
    acknowledgedUnavailableTeams,
    pendingAvailable,
    pendingUnavailableCount,
    hiddenResponsesCount,
    rejected,
    expired
  };
};

const InfoMessage = ({ messageI18nKey, count }) => {
  if (count === 0) return null;
  return (
    <ListGroup.Item variant="info">
      <IconCircleInfo />
      <span className="ms-2">{I18n.t(messageI18nKey, { count })}</span>
    </ListGroup.Item>
  );
};

const ResponderItem = ({
  responder,
  showEstimatedTimeOfArrival,
  showAvailabilityAtIncidentCreation,
  showNicknameInsteadOfFullName
}) => (
  <ListGroup.Item variant={statusToStyle[responder.status]} className="responder-item">
    <Row>
      <Col lg={7} md={7} sm={7} className="text-truncate">
        <IncidentResponderStatusIcon status={responder.status} fixedWidth={true} />
        <span className="ms-2" style={responder.activeStandbyDutyFunctions.length > 0 ? onDutyStyle : {}}>
          {showNicknameInsteadOfFullName ? responder.user_nickname : responder.user_name}{' '}
          <SkillIconList skills={responder.activeStandbyDutyFunctions} />
        </span>
        {responder.do_not_disturb_at_incident_creation && <DoNotDisturbIcon />}
        {showEstimatedTimeOfArrival && responder.status === 'acknowledged' && responder.estimated_time_of_arrival && (
          <Badge className="ms-2">
            <IconPersonRunning /> ETA {secondsToHumanTime(responder.estimated_time_of_arrival)}
          </Badge>
        )}
      </Col>
      {showAvailabilityAtIncidentCreation && (
        <Col lg={1} md={1} sm={1}>
          {responder.status === 'acknowledged' && (
            <AvailabilityIcon available={responder.available_at_incident_creation} />
          )}
        </Col>
      )}
      <Col>
        <SkillIconList skills={responder.skills} />
      </Col>
    </Row>
  </ListGroup.Item>
);

const CrewConfirmations = ({
  displayedIncident,
  dashboardAPIManager,
  hideRejectedAndExpiredResponses,
  hidePendingResponses,
  showAvailabilityAtIncidentCreation,
  incidentSkillAssignments,
  showSeatAllocation,
  showNicknameInsteadOfFullName
}) => {
  let responders = useMemo(
    () => displayedIncident?.incident_responses || [],
    // avoid infinite update loop by doing a deep equals
    [JSON.stringify(displayedIncident?.incident_responses)] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // load missing memberships, skills and duty functions for the active incident
  useEffect(() => {
    if (responders.length === 0) return;

    const skillIds = responders.flatMap(r => r.skill_ids);
    dashboardAPIManager.fetchSkills({ ids: skillIds, onlyMissing: true });

    const membershipIds = responders.map(r => r.membership_id);
    dashboardAPIManager.fetchMemberships({ ids: membershipIds, onlyMissing: true });

    const dutyFunctionIds = responders.flatMap(r => r.active_duty_function_ids);
    dashboardAPIManager.fetchStandbyDutyFunctions({ ids: dutyFunctionIds, onlyMissing: true });
  }, [responders, dashboardAPIManager]);

  if (!displayedIncident) return <WaitingForIncidentMessage />;

  if (responders.length === 0) {
    return (
      <ListGroup>
        <ListGroup.Item>{I18n.t('dashboards.widget_archetypes.crew_confirmations.no_responses')}</ListGroup.Item>
      </ListGroup>
    );
  }

  // Split responders into groups
  const {
    assigned,
    acknowledgedAvailableTeams,
    acknowledgedUnavailableTeams,
    pendingAvailable,
    pendingUnavailableCount,
    hiddenResponsesCount,
    rejected,
    expired
  } = splitIntoGroups(
    responders,
    incidentSkillAssignments,
    hideRejectedAndExpiredResponses,
    hidePendingResponses,
    showSeatAllocation
  );

  const translations = I18n.t('classy_enum.incidents/incident_response_status');

  const showEstimatedTimeOfArrival = responders.some(r => r.estimated_time_of_arrival && r.status === 'acknowledged');

  return (
    <>
      <ResponderList
        responders={assigned}
        title={I18n.t('dashboards.widget_archetypes.incident_skill_assignments.title')}
        variant="default"
        showEstimatedTimeOfArrival={showEstimatedTimeOfArrival}
        showAvailabilityAtIncidentCreation={showAvailabilityAtIncidentCreation}
        showNicknameInsteadOfFullName={showNicknameInsteadOfFullName}
        showCount={false}
      />
      {assigned.length > 0 && <hr />}
      <ResponderList
        responders={acknowledgedAvailableTeams}
        title={translations.acknowledged}
        variant="success"
        showEstimatedTimeOfArrival={showEstimatedTimeOfArrival}
        showAvailabilityAtIncidentCreation={showAvailabilityAtIncidentCreation}
        showNicknameInsteadOfFullName={showNicknameInsteadOfFullName}
      />
      <ResponderList
        responders={acknowledgedUnavailableTeams}
        showEstimatedTimeOfArrival={showEstimatedTimeOfArrival}
        title={translations.acknowledged_unavailable_team}
        variant="success"
        showAvailabilityAtIncidentCreation={showAvailabilityAtIncidentCreation}
        showNicknameInsteadOfFullName={showNicknameInsteadOfFullName}
      />
      <ResponderList
        responders={pendingAvailable}
        title={translations.pending}
        showEstimatedTimeOfArrival={showEstimatedTimeOfArrival}
        showAvailabilityAtIncidentCreation={showAvailabilityAtIncidentCreation}
        showNicknameInsteadOfFullName={showNicknameInsteadOfFullName}
      />

      {(pendingUnavailableCount > 0 || hiddenResponsesCount > 0) && (
        <ListGroup className="mb-1">
          <InfoMessage messageI18nKey="incidents.monitor.unavailable_responses_info" count={pendingUnavailableCount} />
          <InfoMessage
            messageI18nKey="dashboards.widget_archetypes.crew_confirmations.hidden_rejected_and_expired_responses_info"
            count={hiddenResponsesCount}
          />
        </ListGroup>
      )}

      <ResponderList
        responders={expired}
        title={translations.expired}
        showEstimatedTimeOfArrival={showEstimatedTimeOfArrival}
        showAvailabilityAtIncidentCreation={showAvailabilityAtIncidentCreation}
        showNicknameInsteadOfFullName={showNicknameInsteadOfFullName}
      />
      <ResponderList
        responders={rejected}
        title={translations.rejected}
        variant="danger"
        showEstimatedTimeOfArrival={showEstimatedTimeOfArrival}
        showAvailabilityAtIncidentCreation={showAvailabilityAtIncidentCreation}
        showNicknameInsteadOfFullName={showNicknameInsteadOfFullName}
      />
    </>
  );
};

Object.assign(CrewConfirmations, {
  defaultSettings: {
    defaultAutoScrollEnabled: true,
    hideRejectedAndExpiredResponses: false,
    hidePendingResponses: false,
    showAvailabilityAtIncidentCreation: true,
    showSeatAllocation: false
  },
  SettingsContent: Settings
});

const mapStateToProps = state => ({
  displayedIncident: selectCurrentDisplayedIncident(state),
  incidentSkillAssignments: selectIncidentSkillAssignments(state)
});

// Export without redux for testing
export { CrewConfirmations };

export default connect(mapStateToProps)(CrewConfirmations);
